import { useRef, useState } from 'react'

/**
 * Paylaşılan çoklu dosya seçici (Reuse Mandate). Görsel: mockup'ın sade kesikli dropzone'u + seçilen dosya
 * listesi (ad/boyut/kaldır), marka token'larıyla (Ant default renk/font DEĞİL — research D9). Kontrollü:
 * seçili dosyalar `files`, değişiklik `onChange` ile dışarıda tutulur. İstemci tarafı ön-doğrulama minimaldir;
 * kesin doğrulama backend'de (allowlist + boyut).
 */
export interface FileUploadProps {
  files: File[]
  onChange: (files: File[]) => void
  /** input accept (ör. ".pdf,.doc,.docx,..."). */
  accept?: string
  label?: string
  hint?: string
  /** false → seçilen dosyaların yerleşik listesi gizlenir (çağıran kendi tablosunu render eder). Varsayılan true. */
  showList?: boolean
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function FileUpload({
  files,
  onChange,
  accept,
  label,
  hint,
  showList = true,
}: Readonly<FileUploadProps>) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  // accept uzantı listesi (ör. ".pdf,.doc") — sürükle-bırakta da uygulanır (input `accept` yalnız diyaloğu
  // süzer). Boşsa süzme yok. Kesin doğrulama yine backend'de.
  const acceptExts = accept
    ? accept
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s.startsWith('.'))
    : []
  const isAccepted = (f: File) =>
    acceptExts.length === 0 || acceptExts.some((ext) => f.name.toLowerCase().endsWith(ext))

  const addFiles = (selected: FileList | null) => {
    if (!selected) return
    const incoming = Array.from(selected).filter(isAccepted)
    // Aynı ad+boyut ikilisini yinelenmiş sayıp ele (aynı dosyayı iki kez eklemeyi önle).
    const key = (f: File) => `${f.name}:${f.size}`
    const existing = new Set(files.map(key))
    const merged = [...files, ...incoming.filter((f) => !existing.has(key(f)))]
    onChange(merged)
    if (inputRef.current) inputRef.current.value = ''
  }

  const onDrop = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setDragging(false)
    addFiles(e.dataTransfer.files)
  }

  const removeAt = (index: number) => onChange(files.filter((_, i) => i !== index))

  return (
    <div className="ui-fileupload">
      <button
        type="button"
        className={`ui-fileupload-dropzone${dragging ? ' dragging' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          setDragging(false)
        }}
        onDrop={onDrop}
      >
        <i className="pi pi-paperclip" aria-hidden /> {label ?? 'Dosya ekle'}
      </button>
      {hint && <div className="ui-fileupload-hint">{hint}</div>}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => addFiles(e.target.files)}
      />
      {showList && files.length > 0 && (
        <ul className="ui-fileupload-list">
          {files.map((f, i) => (
            <li key={`${f.name}:${f.size}:${i}`} className="ui-fileupload-item">
              <span className="ui-fileupload-name">
                <i className="pi pi-file" aria-hidden /> {f.name}
              </span>
              <span className="ui-fileupload-size">{formatSize(f.size)}</span>
              <button
                type="button"
                className="ui-fileupload-remove"
                aria-label="Kaldır"
                onClick={() => removeAt(i)}
              >
                <i className="pi pi-times" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
